import { useMemo, useState, type ReactNode } from 'react';
import { Button, Divider, Form, type FormInstance } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { FieldDefinitionDto } from '@crm/shared';
import { FieldFormItem, fieldName } from './DynamicField';
import { isEditable } from '../features/fields/system-keys';
import { splitFieldGroups, viewMoreLabel, type FieldGroups } from './ViewMore';

/**
 * Renders all editable fields of an object grouped by their `group`, with "more information" fields in a
 * collapsed "View more" section at the bottom. Pass hidden keys to skip some, and `groupExtras` for full-width
 * content at the end of a group (e.g. the contact's email/phone editors).
 */
export function RecordForm({ defs, form, hidden = [], groupExtras = {} }: { defs: FieldDefinitionDto[]; form: FormInstance; hidden?: string[]; groupExtras?: Record<string, ReactNode> }) {
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => splitFieldGroups(defs, (d) => !isEditable(d) || hidden.includes(d.key)), [defs, hidden]);
  const mainGroups = new Set(groups.main.map(([g]) => g));
  const moreCount = groups.more.reduce((n, [, f]) => n + f.length, 0);

  const renderGroups = (list: FieldGroups, extras: boolean) =>
    list.map(([group, fields], i) => (
      <div key={group}>
        <Divider titlePlacement="start" style={{ marginTop: i === 0 ? 0 : 8 }}>
          {group}
        </Divider>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 16 }}>
          {fields.map((d) => (
            <div key={d.key} style={{ gridColumn: d.type === 'TEXTAREA' ? '1 / -1' : undefined }}>
              <FieldFormItem def={d} />
            </div>
          ))}
        </div>
        {/* extras go with the main section unless their group only exists under "View more" */}
        {(extras || !mainGroups.has(group)) && groupExtras[group]}
      </div>
    ));

  return (
    <>
      {renderGroups(groups.main, true)}
      {moreCount > 0 && (
        // The fields stay mounted while collapsed so their values are kept and validated; a failed one opens the section.
        <Form.Item noStyle shouldUpdate>
          {() => {
            const failed = groups.more.some(([, fields]) => fields.some((d) => form.getFieldError(fieldName(d)).length > 0));
            const shown = open || failed;
            return (
              <>
                <Button type="link" icon={shown ? <UpOutlined /> : <DownOutlined />} iconPlacement="end" style={{ padding: 0, marginBottom: 16 }} onClick={() => setOpen(!shown)}>
                  {viewMoreLabel(shown, moreCount)}
                </Button>
                <div style={{ display: shown ? undefined : 'none' }}>{renderGroups(groups.more, false)}</div>
              </>
            );
          }}
        </Form.Item>
      )}
    </>
  );
}

export { Form };
